import React from "react"
import { format } from "date-fns"

import { FulfillmentType, OrderStatus, type SubOrder } from "@/api/api-client"
import { cn } from "@/lib/utils"

import { VendorOrderAuditHistorySheet } from "./VendorOrderAuditHistorySheet"
import { VendorOrderActions } from "./VendorOrderActions"
import { VendorOrderFinancials } from "./VendorOrderFinancials"
import { VendorOrderIdentity } from "./VendorOrderIdentity"
import { VendorOrderItemsAccordion } from "./VendorOrderItemsAccordion"
import { VendorOrderPipelineSection } from "./VendorOrderPipelineSection"
import type { VendorOrderLabels } from "./vendor-order-types"

interface VendorOrderCardProps {
  subOrder: SubOrder
  labels: VendorOrderLabels
  weightLabel: string
  dateFormat: string
  highlighted?: boolean
  shippingInput: string
  isQuoting: boolean
  isWarehouseExpanded: boolean
  onToggleWarehouse: () => void
  onShippingInputChange: (subOrderId: number, value: string) => void
  onQuoteShipping: (subOrderId: number) => void
  onStatusChange: (subOrderId: number, status: OrderStatus) => void
}

export function VendorOrderCard({
  subOrder,
  labels,
  weightLabel,
  dateFormat,
  highlighted = false,
  shippingInput,
  isQuoting,
  isWarehouseExpanded,
  onToggleWarehouse,
  onShippingInputChange,
  onQuoteShipping,
  onStatusChange,
}: VendorOrderCardProps) {
  const [isAuditOpen, setIsAuditOpen] = React.useState(false)
  const masterOrder = subOrder.masterOrder
  if (!masterOrder) return null

  const currentStatus = subOrder.status as OrderStatus
  const isPickup = subOrder.fulfillmentType === FulfillmentType.Pickup
  const displayStatus =
    isPickup && currentStatus === OrderStatus.ShippingQuoted
      ? OrderStatus.StockVerified
      : currentStatus

  const totalUnits = subOrder.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0
  const totalWeight =
    subOrder.items?.reduce((sum, item) => {
      const w = (item as any).weight ?? 0
      return sum + w * (item.quantity || 0)
    }, 0) || 0
  const orderDate = subOrder.createdAt ?? masterOrder.orderDate
  const formattedOrderDate = orderDate ? format(new Date(orderDate), dateFormat) : "N/A"

  return (
    <div
      id={`order-${subOrder.id}`}
      className={cn(
        "flex flex-col overflow-hidden rounded-xl border bg-background shadow-sm transition-all duration-500",
        highlighted ? "border-accent ring-2 ring-accent ring-offset-2" : "border-border"
      )}
    >
      <div className="grid grid-cols-1 divide-y divide-border lg:grid-cols-4 lg:divide-x lg:divide-y-0">
        <VendorOrderIdentity
          masterOrder={masterOrder}
          subOrderId={subOrder.id}
          displayStatus={displayStatus}
          formattedOrderDate={formattedOrderDate}
          totalWeight={totalWeight}
          totalUnits={totalUnits}
          isPickup={isPickup}
          labels={labels}
          weightLabel={weightLabel}
        />

        <VendorOrderPipelineSection
          subOrder={subOrder}
          title={labels.pipeline || "LOGISTICS PROGRESS"}
          statusLabels={labels.statuses}
        />

        <VendorOrderFinancials subOrder={subOrder} labels={labels} />

        <div className="flex flex-col space-y-6 p-6">
          <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            {labels.actionsRequired || "ACTIONS REQUIRED"}
          </span>
          <div className="flex flex-1 flex-col justify-center gap-3">
            <VendorOrderActions
              subOrder={subOrder}
              labels={labels.actions}
              shippingInput={shippingInput}
              isQuoting={isQuoting}
              terminalLabel={labels.noActions}
              rejectedLabel={labels.orderRejected}
              onShippingInputChange={onShippingInputChange}
              onQuoteShipping={onQuoteShipping}
              onStatusChange={onStatusChange}
            />
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setIsAuditOpen(true)}
                className="text-xs font-bold uppercase tracking-widest text-muted-foreground transition-colors hover:text-primary"
              >
                {labels.viewAuditLog}
              </button>
            </div>
          </div>
        </div>
      </div>

      <VendorOrderItemsAccordion
        subOrder={subOrder}
        masterOrder={masterOrder}
        labels={labels}
        isWarehouseExpanded={isWarehouseExpanded}
        onToggleWarehouse={onToggleWarehouse}
      />

      <VendorOrderAuditHistorySheet
        subOrder={subOrder}
        open={isAuditOpen}
        onOpenChange={setIsAuditOpen}
        statusLabels={labels.statuses}
        dateFormat={dateFormat}
      />
    </div>
  )
}
